import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, TrendingUp, Users, Shield, AlertTriangle, ArrowRightLeft, Cake, FileText, Camera, MessageSquare, Activity, RefreshCw, Handshake, Calendar as CalendarIcon, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const FootballPitch = ({ players }) => {
  const navigate = useNavigate();
  
  // Count players by position
  const positionCounts = {};
  players?.forEach(p => {
    if (p.position) {
      positionCounts[p.position] = (positionCounts[p.position] || 0) + 1;
    }
    if (p.position2) {
      positionCounts[p.position2] = (positionCounts[p.position2] || 0) + 1;
    }
  });

  const handlePositionClick = (position) => {
    navigate(`/players?position=${position}`);
  };

  // Position coordinates on the pitch (percentage based)
  const positions = [
    { id: 'GK', label: 'GK', x: 50, y: 90 },
    { id: 'LB', label: 'LB', x: 10, y: 70 },
    { id: 'LCB', label: 'LCB', x: 30, y: 75 },
    { id: 'CB', label: 'CB', x: 50, y: 77 },
    { id: 'RCB', label: 'RCB', x: 70, y: 75 },
    { id: 'RB', label: 'RB', x: 90, y: 70 },
    { id: 'DM', label: 'DM', x: 50, y: 58 },
    { id: 'LCM', label: 'LCM', x: 30, y: 48 },
    { id: 'CM', label: 'CM', x: 50, y: 45 },
    { id: 'RCM', label: 'RCM', x: 70, y: 48 },
    { id: 'AM', label: 'AM', x: 50, y: 30 },
    { id: 'LW', label: 'LW', x: 15, y: 22 },
    { id: 'RW', label: 'RW', x: 85, y: 22 },
    { id: 'CF', label: 'CF', x: 50, y: 12 },
  ];

  return (
    <div className="glass-card p-6">
      <h2 className="text-2xl font-semibold uppercase tracking-tight text-white mb-4">Squad Positions</h2>
      <p className="text-sm text-zinc-400 mb-4">Click on a position to view players</p>
      
      {/* Football Pitch */}
      <div className="relative w-full" style={{ paddingBottom: '140%' }}>
        <div 
          className="absolute inset-0 rounded-lg overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1a472a 0%, #2d5a3d 50%, #1a472a 100%)',
            border: '3px solid rgba(255,255,255,0.3)'
          }}
        >
          {/* Pitch markings */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 140" preserveAspectRatio="none">
            {/* Center line */}
            <line x1="0" y1="70" x2="100" y2="70" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            {/* Center circle */}
            <circle cx="50" cy="70" r="12" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <circle cx="50" cy="70" r="0.8" fill="rgba(255,255,255,0.4)" />
            
            {/* Top penalty area */}
            <rect x="20" y="0" width="60" height="22" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <rect x="32" y="0" width="36" height="8" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <circle cx="50" cy="15" r="0.8" fill="rgba(255,255,255,0.4)" />
            {/* Top penalty arc */}
            <path d="M 32 22 Q 50 30 68 22" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            
            {/* Bottom penalty area */}
            <rect x="20" y="118" width="60" height="22" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <rect x="32" y="132" width="36" height="8" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <circle cx="50" cy="125" r="0.8" fill="rgba(255,255,255,0.4)" />
            {/* Bottom penalty arc */}
            <path d="M 32 118 Q 50 110 68 118" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            
            {/* Corner arcs */}
            <path d="M 0 3 Q 3 3 3 0" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <path d="M 97 0 Q 97 3 100 3" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <path d="M 0 137 Q 3 137 3 140" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
            <path d="M 97 140 Q 97 137 100 137" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.3" />
          </svg>

          {/* Position markers */}
          {positions.map((pos) => {
            const count = positionCounts[pos.countAs || pos.label] || 0;
            const hasPlayers = count > 0;
            
            return (
              <button
                key={pos.id}
                onClick={() => handlePositionClick(pos.countAs || pos.label)}
                data-testid={`pitch-position-${pos.id}`}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 group ${
                  hasPlayers ? 'hover:scale-110' : 'opacity-50'
                }`}
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              >
                <div className={`relative flex flex-col items-center`}>
                  {/* Position circle */}
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-xs md:text-sm border-2 transition-all ${
                    hasPlayers 
                      ? 'bg-white text-black border-[#FFFFFF] group-hover:shadow-lg group-hover:shadow-[#FFFFFF]/30' 
                      : 'bg-white/10 text-white/50 border-white/20'
                  }`}>
                    {pos.label}
                  </div>
                  {/* Player count badge */}
                  {hasPlayers && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-black rounded-full flex items-center justify-center text-xs font-bold text-white border border-[#FFFFFF]/50">
                      {count}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, playersRes, activityRes, meetingsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/players'),
        api.get('/dashboard/activity-feed').catch(() => ({ data: [] })),
        api.get('/club-meetings').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setPlayers(playersRes.data);
      setActivityFeed(activityRes.data);
      setRecentMeetings(meetingsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Failed to load dashboard data</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            Dashboard
          </h1>
          <p className="text-lg text-zinc-300">Welcome to your agency command center</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Brand Banner */}
        <div className="glass-card p-6 mb-8 border border-white/5 relative overflow-hidden" data-testid="dashboard-welcome-banner">
          <div className="absolute inset-0 bg-gradient-to-r from-white/[0.03] to-transparent pointer-events-none"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-1">DC Sports & Talent</p>
              <p className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">Player First. Opportunity Always.</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2">Players &nbsp;|&nbsp; Partnerships &nbsp;|&nbsp; Performance</p>
            </div>
            <div className="hidden md:block text-right">
              <p className="text-lg italic text-zinc-400">More Than Football</p>
            </div>
          </div>
        </div>

        {/* Stats Grid - 4 cards now */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass-card p-6 card-hover" data-testid="stat-total-players">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/10 rounded-lg">
                <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" strokeWidth={1.5} />
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Total Players</p>
            <p className="text-4xl font-bold tabular-nums text-white">{stats.total_players}</p>
          </div>

          <div className="glass-card p-6 card-hover" data-testid="stat-total-clubs">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Shield className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Total Clubs</p>
            <p className="text-4xl font-bold tabular-nums text-white">{stats.total_clubs}</p>
          </div>

          <div className="glass-card p-6 card-hover" data-testid="stat-avg-age">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <Users className="w-6 h-6 text-purple-400" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Avg Player Age</p>
            <p className="text-4xl font-bold tabular-nums text-white">{stats.avg_player_age}</p>
          </div>

          <div className="glass-card p-6 card-hover" data-testid="stat-completed-deals">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-400" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Total Agency Fees</p>
            <p className="text-4xl font-bold tabular-nums text-white">£{(stats.total_agency_fees || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Negotiation Pipeline Summary */}
        {stats.renegotiation_pipeline && (
          Object.values(stats.renegotiation_pipeline).some(v => v > 0) && (
          <div className="glass-card p-6 mb-8" data-testid="renegotiation-pipeline-card">
            <h2 className="text-2xl font-semibold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
              <RefreshCw className="w-6 h-6 text-white" strokeWidth={1.5} />
              Negotiation Pipeline
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'start_conversation', label: 'Start', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                { key: 'in_progress', label: 'In Progress', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
                { key: 'agreed', label: 'Agreed', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
              ].map(stage => (
                <div key={stage.key} className={`p-4 rounded-lg border ${stage.border} ${stage.bg}`} data-testid={`pipeline-${stage.key}`}>
                  <p className={`text-3xl font-bold tabular-nums ${stage.color}`}>
                    {stats.renegotiation_pipeline[stage.key] || 0}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{stage.label}</p>
                </div>
              ))}
            </div>
          </div>
          )
        )}

        {/* Completed Deals Breakdown */}
        {stats.completed_deals_breakdown && (
          <div className="glass-card p-6 mb-8" data-testid="completed-deals-breakdown">
            <h2 className="text-2xl font-semibold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-orange-400" strokeWidth={1.5} />
              Completed Deals
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'summer_deal', label: 'Summer Deals', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
                { key: 'winter_deal', label: 'Winter Deals', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                { key: 'loan', label: 'Loans', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
              ].map(stage => (
                <div key={stage.key} className={`p-4 rounded-lg border ${stage.border} ${stage.bg}`} data-testid={`deals-${stage.key}`}>
                  <p className={`text-3xl font-bold tabular-nums ${stage.color}`}>
                    {stats.completed_deals_breakdown[stage.key] || 0}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{stage.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Grid - Pitch + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Football Pitch - Takes 1 column */}
          <div className="lg:col-span-1">
            <FootballPitch players={players} />
          </div>

          {/* Alerts Panel - Takes 2 columns */}
          <div className="lg:col-span-2">
            {((stats.expiring_contracts && stats.expiring_contracts.length > 0) || 
              (stats.upcoming_birthdays && stats.upcoming_birthdays.length > 0) ||
              (stats.expiring_representations && stats.expiring_representations.length > 0)) ? (
              <div className="glass-card p-6 h-full">
                <h2 className="text-2xl font-semibold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-white" strokeWidth={1.5} />
                  Alerts & Notifications
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contract Expiry Alerts */}
                  {stats.expiring_contracts && stats.expiring_contracts.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-white" strokeWidth={1.5} />
                        Contract Expiring Soon
                      </h3>
                      <div className="space-y-3">
                        {stats.expiring_contracts.slice(0, 3).map((alert) => {
                          const urgency = alert.days_until_expiry <= 30 ? 'critical' : alert.days_until_expiry <= 60 ? 'warning' : 'info';
                          return (
                            <Link
                              key={alert.player.id}
                              to={`/players/${alert.player.id}`}
                              data-testid={`contract-alert-${alert.player.id}`}
                              className="block p-4 bg-zinc-900/40 border border-white/5 hover:border-white/10 rounded-lg transition-all duration-200"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-bold text-white">{alert.player.name}</p>
                                  <p className="text-sm text-zinc-400">{alert.player.position} • {formatDate(alert.expiry_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-2xl font-bold tabular-nums ${
                                    urgency === 'critical' ? 'text-red-400' :
                                    urgency === 'warning' ? 'text-white' :
                                    'text-blue-400'
                                  }`}>{alert.days_until_expiry}</p>
                                  <p className="text-xs uppercase tracking-widest text-zinc-500">Days</p>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Birthday Alerts */}
                  {stats.upcoming_birthdays && stats.upcoming_birthdays.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                        <Cake className="w-5 h-5 text-pink-400" strokeWidth={1.5} />
                        Upcoming Birthdays
                      </h3>
                      <div className="space-y-3">
                        {stats.upcoming_birthdays.slice(0, 3).map((alert) => {
                          const isToday = alert.days_until_birthday === 0;
                          return (
                            <Link
                              key={alert.player.id}
                              to={`/players/${alert.player.id}`}
                              data-testid={`birthday-alert-${alert.player.id}`}
                              className="block p-4 bg-zinc-900/40 border border-white/5 hover:border-white/10 rounded-lg transition-all duration-200"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 flex items-center gap-3">
                                  <Cake className={`w-5 h-5 ${isToday ? 'text-pink-400' : 'text-purple-400'}`} strokeWidth={1.5} />
                                  <div>
                                    <p className="font-bold text-white">{alert.player.name}</p>
                                    <p className="text-sm text-zinc-400">
                                      {isToday ? '🎉 Birthday Today!' : `Turning ${alert.age_turning}`}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={`text-2xl font-bold tabular-nums ${isToday ? 'text-pink-400' : 'text-purple-400'}`}>
                                    {isToday ? '🎂' : alert.days_until_birthday}
                                  </p>
                                  {!isToday && <p className="text-xs uppercase tracking-widest text-zinc-500">Days</p>}
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Representation Contract Alerts */}
                  {stats.expiring_representations && stats.expiring_representations.length > 0 && (
                    <div className="md:col-span-2">
                      <h3 className="text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
                        Representation Expiring
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {stats.expiring_representations.slice(0, 4).map((alert) => {
                          const urgency = alert.days_until_expiry <= 14 ? 'critical' : alert.days_until_expiry <= 30 ? 'warning' : 'info';
                          return (
                            <Link
                              key={alert.player.id}
                              to={`/players/${alert.player.id}`}
                              data-testid={`representation-alert-${alert.player.id}`}
                              className="block p-4 bg-orange-500/5 border border-orange-500/20 hover:border-orange-500/40 rounded-lg transition-all duration-200"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-bold text-white">{alert.player.name}</p>
                                  <p className="text-sm text-zinc-400">{alert.player.position} • {formatDate(alert.expiry_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-2xl font-bold tabular-nums ${
                                    urgency === 'critical' ? 'text-red-400' :
                                    urgency === 'warning' ? 'text-orange-400' :
                                    'text-yellow-400'
                                  }`}>{alert.days_until_expiry}</p>
                                  <p className="text-xs uppercase tracking-widest text-zinc-500">Days</p>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="glass-card p-6 h-full flex items-center justify-center">
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="text-zinc-500">No alerts at this time</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Club Meetings Panel */}
        <div className="glass-card p-6 mb-8" data-testid="dashboard-club-meetings">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Handshake className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold uppercase tracking-tight text-white">Recent Club Meetings</h2>
            </div>
            <Link
              to="/club-meetings"
              data-testid="view-all-meetings-button"
              className="flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-4 py-2 text-sm transition-all duration-200"
            >
              View All
            </Link>
          </div>
          {recentMeetings.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No club meetings yet. <Link to="/club-meetings" className="text-white hover:underline">Add your first meeting</Link></p>
          ) : (
            <div className="space-y-3">
              {recentMeetings.map((meeting) => (
                <Link
                  key={meeting.id}
                  to="/club-meetings"
                  data-testid={`dashboard-meeting-${meeting.id}`}
                  className="block p-4 bg-zinc-900/40 border border-white/5 hover:border-white/10 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-bold text-white">{meeting.club_name}</p>
                        <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[10px] font-bold uppercase">{formatDate(meeting.meeting_date)}</span>
                      </div>
                      <p className="text-sm text-zinc-300">Spoke to: <span className="text-purple-400 font-medium">{meeting.spoken_to}</span></p>
                      {meeting.notes && <p className="text-xs text-zinc-500 mt-1 truncate">{meeting.notes}</p>}
                    </div>
                    <CalendarIcon className="w-5 h-5 text-zinc-600 flex-shrink-0" strokeWidth={1.5} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Player Activity Feed */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-white/10 rounded-lg">
              <Activity className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-semibold uppercase tracking-tight text-white">Player Activity Feed</h2>
          </div>
          {activityFeed.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No player activity yet. Activity from player portals will appear here.</p>
          ) : (
            <div className="space-y-3">
              {activityFeed.map((activity, index) => {
                const typeConfig = {
                  note: { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Note' },
                  injury: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Injury' },
                  media: { icon: Camera, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Media' },
                  club_contact: { icon: Shield, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Club Contact' }
                };
                const config = typeConfig[activity.type] || typeConfig.note;
                const Icon = config.icon;
                return (
                  <Link
                    key={`${activity.type}-${index}`}
                    to={activity.player_id ? `/players/${activity.player_id}` : '#'}
                    data-testid={`activity-item-${index}`}
                    className="flex items-start gap-3 p-3 bg-zinc-900/40 border border-white/5 hover:border-white/10 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <div className={`p-2 rounded-lg flex-shrink-0 ${config.bg}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-white text-sm">{activity.player_name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${config.bg} ${config.color}`}>{config.label}</span>
                      </div>
                      <p className="text-sm text-zinc-400 truncate">{activity.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                        <span>{activity.created_by}</span>
                        {activity.created_at && <span>{formatDate(activity.created_at)}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
